// src/pages/caisse/DetailOperation.jsx
import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Button, Badge, Spinner, Alert, 
    Modal, Tooltip, OverlayTrigger, Table
} from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import logo from "../../assets/img/IMG-20250130-WA0008.jpg";

// Fonction utilitaire pour arrondir à 2 décimales
const roundToTwo = (num) => {
    if (isNaN(num) || num === undefined || num === null) return 0;
    return Math.round((num + Number.EPSILON) * 100) / 100;
};

export default function DetailOperation() {
    const navigate = useNavigate();
    const { id } = useParams();
    
    const [operation, setOperation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchOperation();
        fetchSocieteInfos();
        loadLogoBase64();
    }, [id]);

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                img.onerror = () => {
                    console.warn("Impossible de charger le logo");
                    setLogoBase64('');
                    resolve('');
                };
                img.src = logo;
            } catch (error) {
                console.error("Erreur chargement logo:", error);
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const fetchOperation = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setOperation(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSuccessMessage("Opération supprimée avec succès !");
                setShowSuccessAlert(true);
                setTimeout(() => {
                    navigate('/caisse/operations');
                }, 2000);
            } else {
                setError(result.message);
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError("Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com'
    };

    // Fonction pour rediriger vers la page d'ajout avec les informations pré-remplies
   // src/pages/caisse/DetailOperation.jsx
// Seule la fonction handleAddPaiement est modifiée

const handleAddPaiement = () => {
    // Calculer le reste réel en fonction des paiements existants
    const montantTotal = Number(operation.montant_initial) || Number(operation.montant) || 0;
    const avanceInitiale = Number(operation.avance) || 0;
    
    // Somme des paiements déjà effectués
    let sommePaiements = 0;
    const paiementsList = operation.paiements || [];
    for (let i = 0; i < paiementsList.length; i++) {
        sommePaiements += Number(paiementsList[i].avance) || 0;
    }
    
    // Total versé = avance initiale + somme des paiements
    const totalVersé = avanceInitiale + sommePaiements;
    
    // Reste réel à payer
    const resteReel = Math.max(0, montantTotal - totalVersé);
    
    // Pourcentage payé
    const pourcentagePaye = montantTotal > 0 ? (totalVersé / montantTotal) * 100 : 0;
    
    // Ne pas permettre d'ajouter un paiement si déjà soldé
    if (resteReel <= 0) {
        setSuccessMessage("Ce décaissement est déjà soldé. Impossible d'ajouter un nouveau paiement.");
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
        return;
    }
    
    // Stocker les informations du décaissement parent dans sessionStorage
    const paiementData = {
        parent_id: operation.id,
        type: 'paiement',
        beneficiaire_nom: operation.beneficiaire_nom,
        beneficiaire_prenom: operation.beneficiaire_prenom,
        motif: operation.motif,
        montant_initial: montantTotal,
        avance_actuelle: totalVersé,
        reste: resteReel,
        pourcentage_paye: pourcentagePaye,
        numero_bordereau_parent: operation.numero_bordereau,
        date_signature: operation.date_signature,
        fait_a: operation.fait_a
    };
    sessionStorage.setItem('paiementData', JSON.stringify(paiementData));
    
    navigate('/caisse/decaissement', { 
        state: { 
            parent_id: operation.id,
            beneficiaire_nom: operation.beneficiaire_nom,
            beneficiaire_prenom: operation.beneficiaire_prenom,
            motif: operation.motif,
            montant_initial: montantTotal,
            avance_actuelle: totalVersé,
            reste: resteReel,
            pourcentage_paye: pourcentagePaye,
            numero_bordereau_parent: operation.numero_bordereau,
            date_signature: operation.date_signature,
            fait_a: operation.fait_a
        }
    });
};

    // Fonction d'impression pour un paiement spécifique
    const genererHTMLImpressionPaiement = (paiement, montantDuAvant, montantPaye, nouveauReste, montantTotal, avanceInitiale) => {
        const logoSrc = logoBase64;
        console.log(montantDuAvant);
        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Bordereau de paiement ${paiement.numero_bordereau}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Arial', Times, sans-serif; background: white; padding: 0; margin: 0; color: #000; font-size: 11pt; }
                    .bordereau-container { width: 100%; max-width: 210mm; margin: 0 auto; background: white; }
                    .header { padding: 8px 15px; border-bottom: 1px solid #000; display: flex; justify-content: space-between; align-items: center; }
                    .logo-img { height: 45px; width: auto; }
                    .company-name { font-size: 13pt; font-weight: bold; }
                    .document-title { font-size: 15pt; font-weight: bold; text-align: center; }
                    .document-number { font-size: 10pt; text-align: center; }
                    .date { font-size: 10pt; text-align: right; }
                    .contact-bar { padding: 8px 15px; background: #f8f8f8; border-bottom: 1px solid #e0e0e0; display: flex; justify-content: space-between; align-items: center; font-size: 9pt; flex-wrap: wrap; gap: 10px; }
                    .body { padding: 12px 15px; }
                    .section { margin-bottom: 15px; }
                    .section-title { font-size: 11pt; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #000; padding-bottom: 2px; margin-bottom: 8px; }
                    .beneficiary-box { border: 1px solid #ccc; padding: 8px; }
                    .beneficiary-name { font-size: 12pt; font-weight: bold; margin-bottom: 5px; }
                    .beneficiary-motif { font-size: 10pt; padding-top: 5px; border-top: 1px dashed #ccc; }
                    .amounts-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
                    .amounts-table td { border: 1px solid #000; padding: 6px; font-size: 10pt; }
                    .amounts-table .label { font-weight: bold; background: #f9f9f9; width: 25%; }
                    .amounts-table .value { font-weight: bold; text-align: right; font-size: 11pt; }
                    .details-table { width: 100%; margin-bottom: 15px; }
                    .details-table td { padding: 5px 0; font-size: 10pt; }
                    .details-table .label { font-weight: bold; width: 30%; }
                    .signature-box { margin-top: 20px; display: flex; justify-content: space-between; }
                    .signature-item { width: 45%; text-align: center; }
                    .signature-line { border-top: 1px solid #000; margin: 20px 0 3px 0; }
                    .signature-label { font-size: 9pt; font-style: italic; }
                    .signature-img { max-width: 130px; max-height: 45px; margin-bottom: 5px; }
                    .footer { padding: 6px 15px; border-top: 1px solid #ddd; background: #f8f8f8; font-size: 8pt; text-align: center; }
                    .text-right { text-align: right; }
                    @media print { .contact-bar, .footer { background: none; } }
                </style>
            </head>
            <body>
                <div class="bordereau-container">
                    <div class="header">
                        <div>${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="logo-img" />` : `<div class="company-name">${company.name}</div>`}</div>
                        <div>
                            <div class="document-title">BORDEREAU DE PAIEMENT</div>
                            <div class="document-number">N° ${paiement.numero_bordereau}</div>
                        </div>
                        <div class="date">${new Date().toLocaleDateString('fr-FR')}</div>
                    </div>
                    
                    <div class="contact-bar">
                        <span>${company.address}</span>
                        <span>${company.phone}</span>
                        <span>${company.email}</span>
                    </div>
                    
                    <div class="body">
                        <div class="section">
                            <div class="section-title">BÉNÉFICIAIRE</div>
                            <div class="beneficiary-box">
                                <div class="beneficiary-name">${paiement.beneficiaire_prenom} ${paiement.beneficiaire_nom.toUpperCase()}</div>
                                <div class="beneficiary-motif"><strong>Motif :</strong> ${paiement.motif}</div>
                            </div>
                        </div>
                        
                        <div class="section">
                            <div class="section-title">DÉTAIL DU PAIEMENT</div>
                            <table class="amounts-table" cellpadding="0" cellspacing="0">
                                <tr>
                                    <td class="label">Montant dû avant paiement</td>
                                    <td class="value">${formatCurrency(montantDuAvant)}</td>
                                    <td class="label">Montant payé</td>
                                    <td class="value">${formatCurrency(montantPaye)}</td>
                                </tr>
                                <tr>
                                    <td class="label" colspan="2">Reste après paiement</td>
                                    <td class="value" colspan="2">${formatCurrency(nouveauReste)}</td>
                                </tr>
                            </table>
                        </div>
                        
                        <div class="section">
                            <div class="section-title">RÉCAPITULATIF DU DÉCAISSEMENT</div>
                            <table class="amounts-table" cellpadding="0" cellspacing="0">
                                <tr>
                                    <td class="label">Montant total initial</td>
                                    <td class="value">${formatCurrency(montantTotal)}</td>
                                    <td class="label">Avance initiale</td>
                                    <td class="value">${formatCurrency(avanceInitiale)}</td>
                                </tr>
                            </table>
                        </div>
                        
                        <div class="signature-box">
                            <div class="signature-item">
                                ${paiement.signature ? `<img src="${paiement.signature}" class="signature-img" />` : '<div class="signature-line"></div>'}
                                <div class="signature-label">Signature du bénéficiaire</div>
                            </div>
                            <div class="signature-item">
                                ${paiement.cachet ? `<img src="${paiement.cachet}" class="signature-img" />` : '<div class="signature-line"></div>'}
                                <div class="signature-label">Cachet de l'entreprise</div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="footer">
                        Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
                    </div>
                </div>
            </body>
            </html>
        `;
    };

    const genererHTMLImpression = () => {
        if (!operation) return '';

        const montantTotal = Number(operation.montant_initial) || Number(operation.montant) || 0;
        const avanceInitiale = Number(operation.avance) || 0;
        const paiementsList = operation.paiements || [];
        let sommePaiements = 0;
        for (let i = 0; i < paiementsList.length; i++) {
            sommePaiements += Number(paiementsList[i].avance) || 0;
        }
        const totalVersé = avanceInitiale + sommePaiements;
        const resteGlobal = montantTotal - totalVersé;
        const logoSrc = logoBase64;

        let historiqueRows = '';
        const resteApresInitial = montantTotal - avanceInitiale;
        historiqueRows += `
            <tr>
                <td class="text-center"><strong>Initial</strong></td>
                <td><code>${operation.numero_bordereau}</code></td>
                <td>${formatDate(operation.created_at)}<br/><small>${new Date(operation.created_at).toLocaleTimeString('fr-FR')}</small></td>
                <td class="text-right"><strong>${formatCurrency(montantTotal)}</strong></td>
                <td class="text-right"><strong>${formatCurrency(avanceInitiale)}</strong></td>
                <td class="text-right"><strong>${formatCurrency(resteApresInitial)}</strong></td>
            </tr>
        `;
        
        if (paiementsList.length > 0) {
            let montantDuAvant = resteApresInitial;
            for (let i = 0; i < paiementsList.length; i++) {
                const p = paiementsList[i];
                const montantPaye = Number(p.avance) || 0;
                const nouveauReste = montantDuAvant - montantPaye;
                historiqueRows += `
                    <tr>
                        <td class="text-center">Paiement ${i + 1}</td>
                        <td><code>${p.numero_bordereau}</code></td>
                        <td>${formatDate(p.created_at)}<br/><small>${new Date(p.created_at).toLocaleTimeString('fr-FR')}</small></td>
                        <td class="text-right">${formatCurrency(montantDuAvant)}</td>
                        <td class="text-right">${formatCurrency(montantPaye)}</td>
                        <td class="text-right">${formatCurrency(nouveauReste)}</td>
                    </tr>
                `;
                montantDuAvant = nouveauReste;
            }
        }

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Bordereau ${operation.numero_bordereau}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Arial', Times, sans-serif; background: white; padding: 0; margin: 0; color: #000; font-size: 11pt; }
                    .bordereau-container { width: 100%; max-width: 210mm; margin: 0 auto; background: white; }
                    .header { padding: 8px 15px; border-bottom: 1px solid #000; display: flex; justify-content: space-between; align-items: center; }
                    .logo-img { height: 45px; width: auto; }
                    .company-name { font-size: 13pt; font-weight: bold; }
                    .document-title { font-size: 15pt; font-weight: bold; text-align: center; }
                    .document-number { font-size: 10pt; text-align: center; }
                    .date { font-size: 10pt; text-align: right; }
                    .contact-bar { padding: 8px 15px; background: #f8f8f8; border-bottom: 1px solid #e0e0e0; display: flex; justify-content: space-between; align-items: center; font-size: 9pt; flex-wrap: wrap; gap: 10px; }
                    .body { padding: 12px 15px; }
                    .section { margin-bottom: 15px; }
                    .section-title { font-size: 11pt; font-weight: bold; text-transform: uppercase; border-bottom: 1px solid #000; padding-bottom: 2px; margin-bottom: 8px; }
                    .beneficiary-box { border: 1px solid #ccc; padding: 8px; }
                    .beneficiary-name { font-size: 12pt; font-weight: bold; margin-bottom: 5px; }
                    .beneficiary-motif { font-size: 10pt; padding-top: 5px; border-top: 1px dashed #ccc; }
                    .amounts-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
                    .amounts-table td { border: 1px solid #000; padding: 6px; font-size: 10pt; }
                    .amounts-table .label { font-weight: bold; background: #f9f9f9; width: 25%; }
                    .amounts-table .value { font-weight: bold; text-align: right; font-size: 11pt; }
                    .details-table { width: 100%; margin-bottom: 15px; }
                    .details-table td { padding: 5px 0; font-size: 10pt; }
                    .details-table .label { font-weight: bold; width: 30%; }
                    .historique-table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 15px; }
                    .historique-table th, .historique-table td { border: 1px solid #000; padding: 6px; font-size: 9pt; }
                    .historique-table th { background: #f0f0f0; font-weight: bold; text-align: center; }
                    .signature-box { margin-top: 20px; display: flex; justify-content: space-between; }
                    .signature-item { width: 45%; text-align: center; }
                    .signature-line { border-top: 1px solid #000; margin: 20px 0 3px 0; }
                    .signature-label { font-size: 9pt; font-style: italic; }
                    .signature-img { max-width: 130px; max-height: 45px; margin-bottom: 5px; }
                    .footer { padding: 6px 15px; border-top: 1px solid #ddd; background: #f8f8f8; font-size: 8pt; text-align: center; }
                    .text-right { text-align: right; }
                    @media print { .contact-bar, .footer { background: none; } }
                </style>
            </head>
            <body>
                <div class="bordereau-container">
                    <div class="header">
                        <div>${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="logo-img" />` : `<div class="company-name">${company.name}</div>`}</div>
                        <div>
                            <div class="document-title">BORDEREAU DE DÉCAISSEMENT</div>
                            <div class="document-number">N° ${operation.numero_bordereau}</div>
                        </div>
                        <div class="date">${new Date().toLocaleDateString('fr-FR')}</div>
                    </div>
                    
                    <div class="contact-bar">
                        <span>${company.address}</span>
                        <span>${company.phone}</span>
                        <span>${company.email}</span>
                    </div>
                    
                    <div class="body">
                        <div class="section">
                            <div class="section-title">BÉNÉFICIAIRE</div>
                            <div class="beneficiary-box">
                                <div class="beneficiary-name">${operation.beneficiaire_prenom} ${operation.beneficiaire_nom.toUpperCase()}</div>
                                <div class="beneficiary-motif"><strong>Motif :</strong> ${operation.motif}</div>
                            </div>
                        </div>
                        
                        <div class="section">
                            <div class="section-title">MONTANTS</div>
                            <table class="amounts-table" cellpadding="0" cellspacing="0">
                                <tr>
                                    <td class="label">Montant total</td>
                                    <td class="value">${formatCurrency(montantTotal)}</td>
                                    <td class="label">Total versé</td>
                                    <td class="value">${formatCurrency(totalVersé)}</td>
                                </tr>
                                <tr>
                                    <td class="label" colspan="2">Reste à payer</td>
                                    <td class="value" colspan="2">${formatCurrency(resteGlobal)}</td>
                                </tr>
                            </table>
                        </div>
                        
                        <div class="section">
                            <div class="section-title">DÉTAILS</div>
                            <table class="details-table" cellpadding="0" cellspacing="0">
                                <tr>
                                    <td class="label">Fait à :</td>
                                    <td>${operation.fait_a || '_______________'}</td>
                                    <td class="label">Le :</td>
                                    <td>${formatDate(operation.date_signature) || formatDate(operation.created_at)}</td>
                                </tr>
                            </table>
                        </div>
                        
                        <div class="section">
                            <div class="section-title">HISTORIQUE DES TRANSACTIONS</div>
                            <table class="historique-table">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Bordereau</th>
                                        <th>Date</th>
                                        <th class="text-right">Montant dû</th>
                                        <th class="text-right">Montant payé</th>
                                        <th class="text-right">Reste</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${historiqueRows}
                                </tbody>
                                <tfoot>
                                    <tr style="background: #f0f0f0; font-weight: bold;">
                                        <td colspan="3" class="text-right">Récapitulatif :</td>
                                        <td class="text-right">${formatCurrency(montantTotal)}</td>
                                        <td class="text-right">${formatCurrency(totalVersé)}</td>
                                        <td class="text-right">${formatCurrency(resteGlobal)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        
                        <div class="signature-box">
                            <div class="signature-item">
                                ${operation.signature ? `<img src="${operation.signature}" class="signature-img" />` : '<div class="signature-line"></div>'}
                                <div class="signature-label">Signature du bénéficiaire</div>
                            </div>
                            <div class="signature-item">
                                ${operation.cachet ? `<img src="${operation.cachet}" class="signature-img" />` : '<div class="signature-line"></div>'}
                                <div class="signature-label">Cachet de l'entreprise</div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="footer">
                        Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
                    </div>
                </div>
            </body>
            </html>
        `;
    };

    const handlePrintPaiement = (htmlContent) => {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
    };

    const handlePrint = () => {
        const htmlContent = genererHTMLImpression();
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
        showSuccess("Document envoyé à l'impression");
    };

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false, loading = false }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}>
            <Button variant={variant} onClick={onClick} disabled={disabled || loading} className="action-btn">
                {loading ? <Spinner size="sm" /> : <i className={icon}></i>}
            </Button>
        </OverlayTrigger>
    );

    if (loading && !operation) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du décaissement...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !operation) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Opération non trouvée"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate(-1)}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    // Calcul des montants
    const montantTotal = Number(operation.montant_initial) || Number(operation.montant) || 0;
    const avanceInitiale = Number(operation.avance) || 0;
    let sommePaiements = 0;
    const paiementsList = operation.paiements || [];
    for (let i = 0; i < paiementsList.length; i++) {
        sommePaiements += Number(paiementsList[i].avance) || 0;
    }
    const totalVersé = avanceInitiale + sommePaiements;
    const resteGlobal = montantTotal - totalVersé;
    const pourcentagePaye = montantTotal > 0 ? (totalVersé / montantTotal) * 100 : 0;
    
    const statut = {
        variant: resteGlobal <= 0 ? 'success' : (totalVersé > 0 ? 'warning' : 'danger'),
        label: resteGlobal <= 0 ? 'Soldé' : (totalVersé > 0 ? 'Avance partielle' : 'Impayé'),
        icon: resteGlobal <= 0 ? 'ri-checkbox-circle-line' : (totalVersé > 0 ? 'ri-time-line' : 'ri-error-warning-line')
    };

    const pourcentageAffichage = pourcentagePaye % 1 === 0 ? pourcentagePaye : pourcentagePaye.toFixed(1);

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}

                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-bank-card-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Détail du décaissement</h4>
                                    <p className="text-muted small mb-0">Consultez les informations du décaissement</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <ActionButton variant="outline-secondary" icon="ri-arrow-left-line" onClick={() => navigate('/caisse/operations')} tooltip="Retour" />
                            <ActionButton variant="outline-primary" icon="ri-printer-line" onClick={handlePrint} tooltip="Imprimer" />
                            <ActionButton variant="outline-danger" icon="ri-delete-bin-line" onClick={() => setShowDeleteModal(true)} tooltip="Supprimer" />
                            <Button 
                                variant="success" 
                                size="sm"
                                onClick={handleAddPaiement}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                disabled={resteGlobal <= 0}
                            >
                                <i className="ri-add-line"></i>
                                Ajouter un décaissement
                            </Button>
                        </div>
                    </div>

                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center">
                                <Col md={4}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-barcode-line text-primary fs-4" style={{ color: '#21ACCD' }}></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Bordereau</small>
                                            <h4 className="mb-0">{operation.numero_bordereau}</h4>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-calendar-line text-success fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Date création</small>
                                            <h5 className="mb-0">{formatDate(operation.created_at)}</h5>
                                            <small className="text-muted">{new Date(operation.created_at).toLocaleTimeString('fr-FR')}</small>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className={`bg-${statut.variant} bg-opacity-10 rounded-circle p-3 me-3`}>
                                            <i className={`${statut.icon} text-${statut.variant} fs-4`}></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Statut</small>
                                            <h5 className={`mb-0 text-${statut.variant}`}>{statut.label}</h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="text-end">
                                        <span className="status-badge" style={{
                                            background: statut.variant === 'success' ? '#10b981' : statut.variant === 'warning' ? '#f59e0b' : '#ef4444',
                                            color: 'white', padding: '0.5rem 1rem', borderRadius: '30px', fontSize: '0.75rem', fontWeight: '600'
                                        }}>
                                            <i className={`${statut.icon} me-1`}></i>
                                            {statut.label}
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-building-line me-2" style={{ color: '#21ACCD' }}></i>Informations société</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold text-primary mb-3">{company.name}</h5>
                                    <p className="mb-1"><i className="ri-map-pin-line me-2"></i>{company.address}</p>
                                    <p className="mb-1"><i className="ri-phone-line me-2"></i>{company.phone}</p>
                                    <p className="mb-1"><i className="ri-mail-line me-2"></i>{company.email}</p>
                                    <p className="mb-0"><i className="ri-global-line me-2"></i>{company.site_web}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-user-line me-2" style={{ color: '#21ACCD' }}></i>Informations bénéficiaire</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3">{operation.beneficiaire_prenom} {operation.beneficiaire_nom}</h5>
                                    <p className="mb-1"><i className="ri-question-line me-2"></i>Motif: {operation.motif}</p>
                                    {operation.fait_a && <p className="mb-1"><i className="ri-map-pin-line me-2"></i>Lieu: {operation.fait_a}</p>}
                                    {operation.date_signature && <p className="mb-0"><i className="ri-calendar-line me-2"></i>Date signature: {formatDate(operation.date_signature)}</p>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Cartes des montants */}
                    <Row className="g-4 mb-4">
                        <Col md={4}>
                            <Card className="border-0 shadow-sm rounded-3 text-center">
                                <Card.Body className="p-4">
                                    <div className="financial-icon mb-3">
                                        <i className="ri-money-euro-circle-line fs-1 text-primary"></i>
                                    </div>
                                    <small className="text-muted">Montant total</small>
                                    <h3 className="fw-bold text-primary mb-0">{formatCurrency(montantTotal)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm rounded-3 text-center">
                                <Card.Body className="p-4">
                                    <div className="financial-icon mb-3">
                                        <i className="ri-hand-coin-line fs-1 text-success"></i>
                                    </div>
                                    <small className="text-muted">Total versé</small>
                                    <h3 className="fw-bold text-success mb-0">{formatCurrency(totalVersé)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm rounded-3 text-center">
                                <Card.Body className="p-4">
                                    <div className="financial-icon mb-3">
                                        <i className="ri-money-euro-circle-line fs-1 text-danger"></i>
                                    </div>
                                    <small className="text-muted">Reste à payer</small>
                                    <h3 className={`fw-bold mb-0 ${resteGlobal > 0 ? 'text-danger' : 'text-success'}`}>{formatCurrency(resteGlobal)}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Barre de progression */}
                    <Row className="g-4 mb-4">
                        <Col lg={12}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-semibold">Progression du paiement</span>
                                        <span className="fw-bold text-primary">{pourcentageAffichage}%</span>
                                    </div>
                                    <div className="progress" style={{ height: '10px', borderRadius: '5px', background: '#e2e8f0' }}>
                                        <div className="progress-bar" style={{ width: `${pourcentagePaye}%`, background: 'linear-gradient(90deg, #10b981, #059669)', borderRadius: '5px' }}></div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

{/* Historique des transactions */}
{paiementsList.length > 0 && (
    <Row className="g-4 mt-2">
        <Col lg={12}>
            <Card className="border-0 shadow-sm rounded-3">
                <Card.Header className="bg-transparent border-0 pt-4">
                    <h6 className="mb-0 fw-semibold">
                        <i className="ri-history-line me-2" style={{ color: '#21ACCD' }}></i>
                        Historique des transactions
                    </h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4" style={{ width: '80px' }}>Type</th>
                                    <th style={{ width: '220px' }}>Bordereau</th>
                                    <th style={{ width: '130px' }}>Date</th>
                                    <th className="text-end" style={{ width: '120px' }}>Montant dû</th>
                                    <th className="text-end" style={{ width: '120px' }}>Avance payée</th>
                                    <th className="text-end" style={{ width: '120px' }}>Reste</th>
                                    <th className="text-center" style={{ width: '70px' }}>Signature</th>
                                    <th className="text-center pe-4" style={{ width: '80px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Transaction initiale */}
                                <tr className="table-primary">
                                    <td className="ps-4">
                                        <Badge bg="primary" className="rounded-pill">
                                            <i className="ri-add-line me-1"></i> Initial
                                        </Badge>
                                    </td>
                                    <td><code>{operation.numero_bordereau}</code></td>
                                    <td>
                                        {formatDate(operation.created_at)}<br />
                                        <small className="text-muted">{new Date(operation.created_at).toLocaleTimeString('fr-FR')}</small>
                                    </td>
                                    {/* Montant dû = montant total */}
                                    <td className="text-end fw-bold">{formatCurrency(montantTotal)}</td>
                                    {/* Avance payée = avance initiale */}
                                    <td className="text-end text-warning fw-bold">{formatCurrency(avanceInitiale)}</td>
                                    {/* Reste = montant total - avance initiale */}
                                    <td className="text-end">
                                        <span className="fw-bold text-danger">{formatCurrency(montantTotal - avanceInitiale)}</span>
                                    </td>
                                    <td className="text-center">
                                        {operation.signature ? <i className="ri-check-line text-success fs-5" title="Signé"></i> : <i className="ri-close-line text-muted" title="Non signé"></i>}
                                    </td>
                                    <td className="text-center pe-4"></td>
                                </tr>
                                
                                {/* Paiements - calcul séquentiel - CORRECTION ICI */}
                               {/* Paiements - calcul séquentiel avec TRI */}
{/* Paiements - calcul séquentiel avec TRI et capture correcte de resteAvant */}
{(() => {
    const paiementRows = [];
    
    // Trier les paiements par date
    const sortedPaiements = [...paiementsList].sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
    );
    
    let resteAvant = montantTotal - avanceInitiale;
    
    for (let idx = 0; idx < sortedPaiements.length; idx++) {
        const paiement = sortedPaiements[idx];
        const montantPaye = Number(paiement.avance) || 0;
        const nouveauReste = Math.max(0, resteAvant - montantPaye);
        
        // CAPTURER la valeur actuelle de resteAvant pour cette itération
        const montantDuAvantIteration = resteAvant;
        
        paiementRows.push(
            <tr key={paiement.id || idx}>
                <td className="ps-4">
                    <Badge bg="info" className="rounded-pill">
                        <i className="ri-bank-card-line me-1"></i> Paiement #{idx + 1}
                    </Badge>
                </td>
                <td><code>{paiement.numero_bordereau}</code></td>
                <td>
                    {formatDate(paiement.created_at)}<br />
                    <small className="text-muted">{new Date(paiement.created_at).toLocaleTimeString('fr-FR')}</small>
                </td>
                <td className="text-end fw-bold">{formatCurrency(montantDuAvantIteration)}</td>
                <td className="text-end text-success fw-bold">{formatCurrency(montantPaye)}</td>
                <td className="text-end">
                    {nouveauReste <= 0 ? (
                        <span className="text-success fw-bold">Soldé</span>
                    ) : (
                        <span className="text-danger fw-bold">{formatCurrency(nouveauReste)}</span>
                    )}
                </td>
                <td className="text-center">
                    {paiement.signature ? <i className="ri-check-line text-success fs-5" title="Signé"></i> : <i className="ri-close-line text-muted" title="Non signé"></i>}
                </td>
                <td className="text-center pe-4">
                    <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        className="rounded-pill px-3" 
                        onClick={() => {
                            // Utiliser la valeur capturée, pas la variable qui change
                            const html = genererHTMLImpressionPaiement(
                                paiement, 
                                montantDuAvantIteration,  // ← valeur capturée correcte
                                montantPaye, 
                                nouveauReste, 
                                montantTotal, 
                                avanceInitiale
                            );
                            handlePrintPaiement(html);
                        }} 
                        title="Imprimer ce paiement"
                    >
                        <i className="ri-printer-line"></i>
                    </Button>
                </td>
            </tr>
        );
        
        // Mettre à jour resteAvant pour le prochain paiement
        resteAvant = nouveauReste;
    }
    
    return paiementRows;
})()}
                            </tbody>
                            <tfoot className="bg-light">
                                <tr>
                                    <td colSpan="3" className="ps-4 fw-semibold">Récapitulatif :</td>
                                    <td className="text-end fw-bold">{formatCurrency(montantTotal)}</td>
                                    <td className="text-end fw-bold text-success">{formatCurrency(totalVersé)}</td>
                                    <td className="text-end fw-bold text-danger">{formatCurrency(resteGlobal)}</td>
                                    <td colSpan="2"></td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    </Row>
)}

                    {(operation.signature || operation.cachet) && (
                        <Row className="g-4 mt-4">
                            {operation.signature && (
                                <Col md={6}>
                                    <Card className="border-0 shadow-sm rounded-3">
                                        <Card.Header className="bg-transparent border-0 pt-4">
                                            <h6 className="mb-0"><i className="ri-pen-line me-2"></i>Signature</h6>
                                        </Card.Header>
                                        <Card.Body className="text-center">
                                            <img src={operation.signature} alt="Signature" style={{ maxWidth: '100%', maxHeight: '150px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px' }} />
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                            {operation.cachet && (
                                <Col md={6}>
                                    <Card className="border-0 shadow-sm rounded-3">
                                        <Card.Header className="bg-transparent border-0 pt-4">
                                            <h6 className="mb-0"><i className="ri-stamp-line me-2"></i>Cachet</h6>
                                        </Card.Header>
                                        <Card.Body className="text-center">
                                            <img src={operation.cachet} alt="Cachet" style={{ maxWidth: '100%', maxHeight: '150px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px' }} />
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    )}

                    <div className="metadata-section mt-4 text-center text-muted small">
                        <i className="ri-information-line me-1"></i>
                        ID: #{operation.id} • Créé le {formatDate(operation.created_at)} à {new Date(operation.created_at).toLocaleTimeString('fr-FR')}
                        {operation.createur_nom && ` • Créé par: ${operation.createur_nom}`}
                    </div>
                </div>
                <Footer />
            </div>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-white">Confirmer la suppression</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-4 text-center">
                    <p className="text-muted mb-3">Êtes-vous sûr de vouloir supprimer ce décaissement ?</p>
                    <div className="bg-light p-3 rounded-3 mb-3">
                        <strong className="text-primary d-block">{operation.numero_bordereau}</strong>
                        <small className="text-muted">{operation.beneficiaire_prenom} {operation.beneficiaire_nom}</small>
                        <div className="mt-1"><strong>{formatCurrency(montantTotal)}</strong></div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="rounded-pill px-4">Annuler</Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting} className="rounded-pill px-5">
                        {deleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : 'Confirmer'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .action-btn { width: 36px; height: 36px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; transition: all 0.2s ease; border: 1px solid #e2e8f0; background: white; color: #64748b; }
                .action-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
                .btn-outline-primary:hover { background: linear-gradient(135deg, #21ACCD, #1a8ba8); border-color: #21ACCD; color: white; }
                .btn-outline-secondary:hover { background: linear-gradient(135deg, #64748b, #475569); border-color: #64748b; color: white; }
                .btn-outline-danger:hover { background: linear-gradient(135deg, #ef4444, #dc2626); border-color: #ef4444; color: white; }
                .rounded-3 { border-radius: 12px !important; }
                .rounded-pill { border-radius: 50px !important; }
                .fs-20 { font-size: 1.25rem; }
                .confirm-icon { animation: scaleIn 0.5s ease-out; }
                @keyframes scaleIn { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
                .alert-modern { border: none; border-radius: 12px; padding: 1rem 1.5rem; box-shadow: 0 4px 8px rgba(0,0,0,0.05); }
                .modern-modal .modal-content { border-radius: 20px; border: none; box-shadow: 0 20px 40px rgba(0,0,0,0.2); overflow: hidden; }
                .financial-icon { width: 60px; height: 60px; margin: 0 auto; display: flex; align-items: center; justify-content: center; background: rgba(33, 172, 205, 0.1); border-radius: 50%; }
                .status-badge { display: inline-flex; align-items: center; gap: 0.5rem; box-shadow: 0 4px 12px rgba(0,0,0,0.15); letter-spacing: 0.3px; }
            `}</style>
        </>
    );
}